// iPhones save photos as HEIC/HEIF by default, which browsers can't render
// natively — they show as a broken-image icon in the dashboard and never
// display once uploaded to Supabase. We convert HEIC/HEIF → JPEG in the browser
// at selection/upload time so every stored brand photo is web-renderable
// regardless of what the client's device produced.

// heic2any pulls in a wasm decoder (~1MB), so load it lazily only when we
// actually encounter a HEIC file — keeps it out of the main bundle.
const isHeic = (file) => {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return true;
  // iOS/desktop sometimes hand us an empty MIME type for HEIC — fall back to
  // the extension.
  return /\.(heic|heif)$/i.test(file.name || '');
};

/**
 * Returns a browser-renderable File. HEIC/HEIF input is converted to JPEG;
 * anything else is passed through untouched. Never throws — on conversion
 * failure it returns the original file so the upload can still proceed.
 */
export async function ensureRenderableImage(file) {
  if (!isHeic(file)) return file;
  try {
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    const newName = (file.name || 'photo').replace(/\.(heic|heif)$/i, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (err) {
    console.error('[heic] conversion failed, using original file:', err?.message || err);
    return file;
  }
}
